import React from 'react';
import { motion } from 'framer-motion';
import { FileTextIcon, ZapIcon, BookOpenIcon, ShieldCheckIcon, LockIcon, CreditCardIcon, StarIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
const badges = [{
  icon: FileTextIcon,
  label: '100% PDF'
}, {
  icon: ZapIcon,
  label: 'Acceso en 5 min'
}, {
  icon: BookOpenIcon,
  label: '3 Tomos + 6 Bonos'
}];
const trust = [{
  icon: ShieldCheckIcon,
  label: 'Garantía 7 días'
}, {
  icon: LockIcon,
  label: 'Pago seguro'
}, {
  icon: CreditCardIcon,
  label: 'Mercado Pago'
}];
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const transition = {
  duration: 0.5,
  ease: 'easeOut' as const
};
export function Hero() {
  return <header className="relative w-full overflow-hidden bg-cream" data-id="element-83" data-mp="nbomx02vuk1c">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[36rem] w-[36rem] rounded-full bg-cream-deep/70 blur-3xl" aria-hidden="true" data-id="element-84" data-mp="3d1sbczku5nu6" />

      <motion.div initial="hidden" animate="visible" transition={{
      staggerChildren: 0.07
    }} className="relative mx-auto grid max-w-6xl gap-x-14 px-5 pb-12 pt-6 sm:px-8 sm:pt-10 lg:grid-cols-[1.05fr_1fr] lg:grid-rows-[auto_auto] lg:items-center lg:pb-20 lg:pt-14" data-id="element-85" data-mp="3j8iv8twpu6s2">
        {/* Promesa (compacta) */}
        <div className="lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-86" data-mp="3e8ecsyc8z1z7">
          <motion.div variants={fadeUp} transition={transition} className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2" data-id="element-87" data-mp="30iawsp3yo4i7">
            <span className="font-display text-base font-semibold tracking-tight text-forest sm:text-lg" data-id="element-88" data-mp="35akmq552t4kh">
              NUEVO NIVEL<sup className="text-[0.6em]" data-id="element-89" data-mp="2violwmblv0ck">™</sup>
            </span>
            <span className="h-4 w-px bg-forest/20" aria-hidden="true" data-id="element-90" data-mp="2vgu0z80c388h" />
            <span className="inline-flex items-center gap-1.5 rounded-full bg-forest/10 px-2.5 py-1 text-[0.7rem] font-semibold text-forest sm:text-xs" data-id="element-91" data-mp="2hkghjtc8owrd">
              🇦🇷 Para Argentina
            </span>
          </motion.div>

          <motion.h1 variants={fadeUp} transition={transition} className="font-display text-[1.7rem] font-semibold leading-[1.12] text-forest-dark sm:text-4xl lg:text-[3.1rem] lg:leading-[1.05]" data-id="element-92" data-mp="s44ifoom7nif">
            El Mega Pack de Comida Celíaca que tu familia estaba esperando.
          </motion.h1>

          <motion.p variants={fadeUp} transition={transition} className="mt-3 max-w-xl text-sm leading-relaxed text-forest-dark/70 sm:text-base lg:mt-5 lg:text-lg" data-id="element-93" data-mp="1b1fcmuve3qr0">
            Recetas, viandas, organización y panadería sin TACC — todo resuelto,
            todo para Argentina.
          </motion.p>
        </div>

        {/* Imagen: en mobile va arriba del botón; en desktop a la derecha */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.97,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut',
        delay: 0.15
      }} className="relative my-5 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:my-0" data-id="element-94" data-mp="3lu9wgyz13ua6">
          <div className="overflow-hidden rounded-2xl border border-forest/10 bg-cream-card shadow-lift lg:rounded-3xl" data-id="element-95" data-mp="21bfs9gsl6zq8">
            <img src="https://cdn.magicpatterns.com/patterns/generated-images/04057cfc-a94b-4170-8675-e244b9a26bd2.jpg" alt="Los tres tomos del Mega Pack sin TACC en formato PDF, vistos en tablet y en versión impresa sobre una mesa de cocina" className="h-40 w-full object-cover sm:h-64 lg:h-auto" loading="eager" data-id="element-96" data-mp="26869w8k1i3dr" />
          </div>
          <div className="absolute -bottom-3 left-3 flex items-center gap-2 rounded-xl border border-forest/10 bg-cream-card px-3 py-2 shadow-soft lg:-bottom-5 lg:left-8 lg:gap-3 lg:rounded-2xl lg:px-4 lg:py-3" data-id="element-97" data-mp="2oynzw5dhmkvf">
            <div className="flex" aria-hidden="true" data-id="element-98" data-mp="zlkke0eooxyf">
              {Array.from({
              length: 5
            }).map((_, i) => <StarIcon key={i} className="h-3.5 w-3.5 fill-wheat text-wheat" data-id="element-99" data-mp="4b49y14g6in5" data-mpi={i} />)}
            </div>
            <p className="text-xs font-semibold text-forest-dark lg:text-sm" data-id="element-100" data-mp="3vign8527mbxs">
              4.9/5{' '}
              <span className="font-normal text-forest-dark/60" data-id="element-101" data-mp="e8imvqr5wpap">
                · +800 familias
              </span>
            </p>
          </div>
        </motion.div>

        {/* CTA + confianza */}
        <div className="lg:col-start-1 lg:row-start-2 lg:self-start" data-id="element-102" data-mp="3hrby3wwgx21p">
          <motion.div variants={fadeUp} transition={transition} className="max-w-md lg:mt-8" data-id="element-103" data-mp="3crfu3xnrtam5">
            <CTAButton data-id="element-104" data-mp="3rgvgl1g9qtw1">🌿 Quiero mi Mega Pack ahora</CTAButton>
            <p className="mt-2.5 text-center text-xs text-forest-dark/50" data-id="element-105" data-mp="3motwwugru0vz">
              www.nuevonivel.shop
            </p>
          </motion.div>

          <motion.ul variants={fadeUp} transition={transition} className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-forest-dark/60 sm:text-sm" data-id="element-106" data-mp="2yxy908ffrpu0">
            {trust.map(({
            icon: Icon,
            label
          }) => <li key={label} className="inline-flex items-center gap-1.5" data-id="element-107" data-mp="1e9ned3iqn30j">
                <Icon className="h-4 w-4 text-forest-light" aria-hidden="true" data-id="element-108" data-mp="zkrc5n3d4k7u" />
                {label}
              </li>)}
          </motion.ul>

          <motion.ul variants={fadeUp} transition={transition} className="mt-4 flex flex-wrap gap-2" data-id="element-109" data-mp="14ju3y5uc0txv">
            {badges.map(({
            icon: Icon,
            label
          }) => <li key={label} className="inline-flex items-center gap-1.5 rounded-full border border-forest/12 bg-cream-card px-3 py-1.5 text-xs font-medium text-forest-dark/80 sm:text-sm" data-id="element-110" data-mp="1x65g8drawjai">
                <Icon className="h-3.5 w-3.5 text-forest-light" aria-hidden="true" data-id="element-111" data-mp="7ss80dg2ib44" />
                {label}
              </li>)}
          </motion.ul>
        </div>
      </motion.div>
    </header>;
}